/**
 * affiliate-page.tsx — Tiếp thị liên kết cho người dùng app. Route `/affiliate` (mục trong menu tài khoản).
 *
 * Chưa là đối tác → giới thiệu chương trình + form đăng ký (đủ thông tin → dùng được ngay).
 * Đã là đối tác → tab: Tổng quan (chỉ số, link + mã, quy tắc) · Khách đã giới thiệu · Hoa hồng · Rút tiền · Hồ sơ & nhận tiền.
 * Backend: ecdp_backend_source/src/modules/affiliate/affiliate-routes.ts.
 */
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, BadgePercent, Check, Copy, Handshake, Loader2, MousePointerClick, Pencil, UserCheck, Users, Wallet } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { ErrorState, Loading, EmptyState } from '@/components/shared/feedback'
import { StatCard } from '@/components/shared/stat-card'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/misc'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { currentLocale } from '@/lib/i18n'
import {
  codeError, discountText, EMPTY_REGISTER_FORM, normalizeCode, programSummary, programClosedText, referralLink, suspendedBanner, tierText,
  validateRegisterForm,
  type PartnerView, type PublicProgram, type RegisterForm,
} from '@/lib/affiliate'
import { useAffiliateMe, useAffiliateSummary, useChangeCode, useRegisterPartner } from '@/hooks/use-affiliate'
import { Field, PartnerTypePicker, PayoutFields, ProfileFields } from './partner-form'
import { CommissionsTab, ProfileTab, ReferralsTab, WithdrawalsTab } from './affiliate-tabs'

const TABS = ['overview', 'customers', 'commissions', 'withdrawals', 'profile'] as const

export function AffiliatePage() {
  const { t } = useTranslation('growth')
  const { data, isLoading, isError, error } = useAffiliateMe()
  const [sp, setSp] = useSearchParams()
  const tab = (TABS as readonly string[]).includes(sp.get('tab') ?? '') ? sp.get('tab')! : 'overview'

  if (isLoading) return <Loading label={t('affiliatePage.loading')} />
  if (isError || !data) return <ErrorState message={apiError(error)} />

  if (data.blockedReason) {
    return (
      <div className="space-y-5">
        <PageHeader title={t('affiliatePage.title')} />
        <EmptyState title={t('affiliatePage.blocked')} description={data.blockedReason} />
      </div>
    )
  }
  if (!data.partner) return <RegisterView program={data.program} />

  const p = data.partner
  return (
    <div className="space-y-5">
      <PageHeader
        title={t('affiliatePage.title')}
        description={t('affiliatePage.description')}
      />
      {p.status === 'suspended' && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <b>{suspendedBanner()}</b>{p.statusReason ? ` ${t('affiliatePage.suspendedReason', { reason: p.statusReason })}` : ''}. {t('affiliatePage.suspendedNote')}
          </span>
        </div>
      )}
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="overview">{t('affiliatePage.tabOverview')}</TabsTrigger>
          <TabsTrigger value="customers">{t('affiliatePage.tabCustomers')}</TabsTrigger>
          <TabsTrigger value="commissions">{t('affiliatePage.tabCommissions')}</TabsTrigger>
          <TabsTrigger value="withdrawals">{t('affiliatePage.tabWithdrawals')}</TabsTrigger>
          <TabsTrigger value="profile">{t('affiliatePage.tabProfile')}</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4"><OverviewTab partner={p} program={data.program} /></TabsContent>
        <TabsContent value="customers" className="mt-4"><ReferralsTab /></TabsContent>
        <TabsContent value="commissions" className="mt-4"><CommissionsTab /></TabsContent>
        <TabsContent value="withdrawals" className="mt-4"><WithdrawalsTab partner={p} program={data.program} /></TabsContent>
        <TabsContent value="profile" className="mt-4"><ProfileTab partner={p} program={data.program} /></TabsContent>
      </Tabs>
    </div>
  )
}

// ── Đăng ký ─────────────────────────────────────────────────────────────

function ProgramCard({ program }: { program: PublicProgram }) {
  const { t } = useTranslation('growth')
  const disc = discountText(program.customerDiscount)
  return (
    <Card className="space-y-3 p-5">
      <div className="flex items-center gap-2">
        <Handshake className="h-5 w-5 text-primary" />
        <p className="font-medium">{t('affiliatePage.programRules')}</p>
      </div>
      <p className="text-sm">{programSummary(program)}</p>
      {program.commission.tiers.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {program.commission.tiers.map((tier) => <li key={tier.name}>{tierText(tier, program.commission.type)}</li>)}
        </ul>
      )}
      {disc && <p className="text-sm"><BadgePercent className="mr-1 inline h-4 w-4 text-primary" />{t('affiliatePage.discountNote', { disc })}</p>}
      <p className="text-xs text-muted-foreground">
        {t('affiliatePage.commissionBaseNote')}
      </p>
    </Card>
  )
}

function RegisterView({ program }: { program: PublicProgram }) {
  const { t } = useTranslation('growth')
  const register = useRegisterPartner()
  const [f, setF] = useState<RegisterForm>(EMPTY_REGISTER_FORM)
  const [errors, setErrors] = useState<Partial<Record<keyof RegisterForm, string>>>({})
  const set = <K extends keyof RegisterForm>(k: K, v: RegisterForm[K]) => setF((s) => ({ ...s, [k]: v }))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const errs = validateRegisterForm(f)
    setErrors(errs)
    if (Object.keys(errs).length) {
      toast.error(t('affiliatePage.checkInfo'))
      return
    }
    register.mutate({ ...f, code: f.code.trim() ? normalizeCode(f.code) : undefined }, {
      onSuccess: () => toast.success(t('affiliatePage.registerSuccess')),
      onError: (err) => toast.error(apiError(err)),
    })
  }

  if (!program.enabled) {
    return (
      <div className="space-y-5">
        <PageHeader title={t('affiliatePage.title')} />
        <EmptyState title={programClosedText()} description={t('affiliatePage.programClosedDesc')} />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PageHeader title={t('affiliatePage.becomePartner')} description={t('affiliatePage.becomePartnerDesc')} />
      <ProgramCard program={program} />
      <form onSubmit={submit} className="space-y-5">
        <Card className="space-y-4 p-5">
          <p className="font-medium">{t('affiliatePage.step1')}</p>
          <PartnerTypePicker value={f.partnerType} onChange={(v) => set('partnerType', v)} />
          <ProfileFields f={f} set={set} errors={errors} />
        </Card>
        <Card className="space-y-4 p-5">
          <p className="font-medium">{t('affiliatePage.step2')}</p>
          <PayoutFields f={f} set={set} errors={errors} allowCommitment08={program.tax.allowCommitment08} />
        </Card>
        <Card className="space-y-4 p-5">
          <p className="font-medium">{t('affiliatePage.step3')}</p>
          <Field label={t('affiliatePage.codeLabel')} error={errors.code} hint={t('affiliatePage.codeHint')}>
            <Input value={f.code} onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder="VD: ANH-BA" maxLength={20} />
          </Field>
          {program.terms && (
            <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs">{program.terms}</div>
          )}
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={f.acceptTerms} onCheckedChange={(v) => set('acceptTerms', v === true)} className="mt-0.5" />
            <span>
              {t('affiliatePage.termsText')}
            </span>
          </label>
          {errors.acceptTerms && <p className="text-xs text-destructive">{errors.acceptTerms}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={register.isPending}>
              {register.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('affiliatePage.registerSubmit')}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  )
}

// ── Tổng quan ───────────────────────────────────────────────────────────

function OverviewTab({ partner, program }: { partner: PartnerView; program: PublicProgram }) {
  const { t } = useTranslation('growth')
  const summary = useAffiliateSummary(true)
  const link = referralLink(window.location.origin, partner.code)
  const s = summary.data
  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(t('affiliatePage.copied', { label }))
    } catch {
      toast.error(t('affiliatePage.copyFailed'))
    }
  }
  return (
    <div className="space-y-5">
      {summary.isLoading ? <Loading /> : s && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard label={t('affiliatePage.stat.clicks')} value={s.clicks} hint={t('affiliatePage.stat.clicksHint', { count: s.clicks30d.toLocaleString(currentLocale()) })} icon={MousePointerClick} />
          <StatCard label={t('affiliatePage.stat.referrals')} value={s.referrals} hint={t('affiliatePage.stat.referralsHint', { count: s.leads.toLocaleString(currentLocale()) })} icon={Users} />
          <StatCard label={t('affiliatePage.stat.payingCustomers')} value={s.payingCustomers} icon={UserCheck} tone="success" />
          <StatCard label={t('affiliatePage.stat.holding')} value={formatVnd(s.commission.holding)} hint={t('affiliatePage.stat.holdingHint', { days: s.holdDays })} icon={Wallet} tone="warning" />
          <StatCard label={t('affiliatePage.stat.available')} value={formatVnd(Math.max(0, s.commission.available))} hint={t('affiliatePage.stat.availableHint', { min: formatVnd(s.minWithdrawal) })} icon={Wallet} tone="success" />
          <StatCard label={t('affiliatePage.stat.paid')} value={formatVnd(s.commission.paid)} hint={t('affiliatePage.stat.paidHint', { pending: formatVnd(s.commission.pendingWithdrawal) })} icon={Wallet} />
        </div>
      )}
      <Card className="space-y-4 p-5">
        <p className="font-medium">{t('affiliatePage.referralLink')}</p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-md border bg-muted/40 px-3 py-2 text-sm">{link}</code>
          <Button variant="outline" onClick={() => copy(link, t('affiliatePage.copyLabelLink'))}><Copy className="h-4 w-4" /> {t('affiliatePage.copyLink')}</Button>
          <Button variant="outline" onClick={() => copy(partner.code, t('affiliatePage.copyLabelCode'))}><Copy className="h-4 w-4" /> {t('affiliatePage.copyCode', { code: partner.code })}</Button>
        </div>
        {partner.status === 'active' && <CodeEditor partner={partner} />}
        <p className="text-xs text-muted-foreground">
          {t('affiliatePage.attributionNote', {
            days: program.cookieDays,
            model: program.attributionModel === 'first_touch' ? t('affiliatePage.attributionFirstTouch') : t('affiliatePage.attributionLastTouch'),
          })}
          {' '}{t('affiliatePage.bindWindowNotePre')}{' '}<b>{partner.code}</b>{' '}{t('affiliatePage.bindWindowNotePost', { days: program.bindWindowDays })}
          {partner.hasPromoCode && program.customerDiscount.mode !== 'off' && (
            <> {t('affiliatePage.promoCodeNotePre')} <b>{t('affiliatePage.promoCodeNoteBold')}</b> {t('affiliatePage.promoCodeNotePost')}</>
          )}
          {' '}{t('affiliatePage.qrNote')}
        </p>
      </Card>
      <ProgramCard program={program} />
    </div>
  )
}

function CodeEditor({ partner }: { partner: PartnerView }) {
  const { t } = useTranslation('growth')
  const change = useChangeCode()
  const [editing, setEditing] = useState(false)
  const [code, setCode] = useState(partner.code)
  const err = code ? codeError(code) : null
  if (!editing) {
    return (
      <Button variant="ghost" size="sm" onClick={() => { setCode(partner.code); setEditing(true) }}>
        <Pencil className="h-3.5 w-3.5" /> {t('affiliatePage.changeCode')}
      </Button>
    )
  }
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input className="max-w-xs" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={20} />
        <Button
          size="sm"
          disabled={!!err || change.isPending || normalizeCode(code) === partner.code}
          onClick={() => change.mutate(normalizeCode(code), {
            onSuccess: () => { toast.success(t('affiliatePage.codeChanged')); setEditing(false) },
            onError: (e) => toast.error(apiError(e)),
          })}
        >
          {change.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t('affiliatePage.save')}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(false)}>{t('affiliatePage.cancel')}</Button>
      </div>
      <p className={err ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
        {err ?? t('affiliatePage.codeChangeHint')}
      </p>
    </div>
  )
}
